import {
    getScopeExtractedFields,
    IExtractedFieldWithMeta,
    IScope,
    PropertySource,
    ScopeStatus,
} from "@/api";
import type {
    ICustomFieldPropertyResponse,
    ICustomFieldResponse,
    IDocumentFieldsByPolicyType,
    IExtractedPropertyValue,
} from "@/api/documents/documents.types";
import { LoadingIndicator } from "@/components/shared/LoadingIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Stack,
    Typography,
} from "@mui/material";
import { PageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { FoundToggleState } from "../../types";
import { DynamicArrayField } from "./DynamicArrayField";
import { DynamicScalarField } from "./DynamicScalarField";
import { AddCustomFieldPopover } from "./AddCustomFieldPopover";
import { useFieldMutations } from "../../hooks/useFieldMutations";

interface IProps {
    scope: IScope;
    search: string;
    foundToggle: FoundToggleState;
    pageNavigationPluginInstance: PageNavigationPlugin;
    currentPage: number;
    totalPages: number;
}

const getFieldCount = (group: IDocumentFieldsByPolicyType) =>
    group.scalar_fields.length + group.array_fields.length;

export const PolicyTypeFieldsSection = ({
    scope,
    search,
    foundToggle,
    pageNavigationPluginInstance,
    currentPage,
    totalPages,
}: IProps) => {
    const isEditable = scope.status === ScopeStatus.COMPLETE;

    const { data, isLoading } = useQuery({
        queryKey: ["scope-extracted-fields", scope.id],
        queryFn: () => getScopeExtractedFields(scope.id),
        enabled: !!scope.id,
    });

    const {
        updateProperty,
        deleteProperty,
        batchDeleteProperties,
        createField,
        deleteField,
        addCFProperty,
        updateCFProperty,
        deleteCFProperty,
    } = useFieldMutations(scope.id, scope.document.id);

    const handleSave = useCallback(
        (
            propertyId: number,
            value: string | null,
            pageNumber?: number | null
        ) => {
            const data: { value: string | null; page_number?: number | null } =
                { value };
            if (pageNumber !== undefined) data.page_number = pageNumber;
            updateProperty.mutate({ propertyId, data });
        },
        [updateProperty]
    );

    const handleDelete = useCallback(
        (propertyId: number) => {
            deleteProperty.mutate(propertyId);
        },
        [deleteProperty]
    );

    const handleDeleteItem = useCallback(
        (
            fieldDefinitionId: number,
            arrayIndex: number,
            expectedDeleteFingerprint: string
        ) => {
            batchDeleteProperties.mutate({
                field_definition_id: fieldDefinitionId,
                array_index: arrayIndex,
                expected_delete_fingerprint: expectedDeleteFingerprint,
            });
        },
        [batchDeleteProperties]
    );

    // Route to the correct mutation based on property source.
    // EditablePropertyRow stays source-agnostic; dispatch happens here.
    const handlePropertySave = useCallback(
        (
            propertyId: number,
            value: string | null,
            pageNumber: number | null,
            source: PropertySource
        ) => {
            if (source === "extracted") {
                updateProperty.mutate({
                    propertyId,
                    data: { value, page_number: pageNumber },
                });
            } else {
                updateCFProperty.mutate({
                    propertyId,
                    data: { value, page_number: pageNumber },
                });
            }
        },
        [updateProperty, updateCFProperty]
    );

    const handlePropertyDelete = useCallback(
        (propertyId: number, source: PropertySource) => {
            if (source === "extracted") {
                deleteProperty.mutate(propertyId);
            } else {
                deleteCFProperty.mutate(propertyId);
            }
        },
        [deleteProperty, deleteCFProperty]
    );

    const handleAddProperty = useCallback(
        (data: {
            customFieldId: number;
            name: string;
            value: string | null;
            pageNumber: number | null;
        }) => {
            addCFProperty.mutate({
                customFieldId: data.customFieldId,
                data: {
                    name: data.name,
                    value: data.value,
                    page_number: data.pageNumber,
                },
            });
        },
        [addCFProperty]
    );

    // Bridge-or-add: for extracted fields, check if a bridge custom field exists,
    // then either add a property to it or create a new bridge atomically.
    const makeAddPropertyToExtractedFieldHandler = useCallback(
        (
            fieldDefinitionId: number,
            policyTypeId: number,
            fieldName: string
        ) => {
            return (submitData: {
                customFieldId: number;
                name: string;
                value: string | null;
                pageNumber: number | null;
            }) => {
                const group = data?.field_groups.find(
                    (g) => g.policy_type_id === policyTypeId
                );
                const bridge = group?.custom_fields?.find(
                    (cf) => cf.field_definition_id === fieldDefinitionId
                );

                if (bridge) {
                    addCFProperty.mutate({
                        customFieldId: bridge.id,
                        data: {
                            name: submitData.name,
                            value: submitData.value,
                            page_number: submitData.pageNumber,
                        },
                    });
                } else {
                    createField.mutate({
                        name: fieldName,
                        policy_type_id: policyTypeId,
                        field_definition_id: fieldDefinitionId,
                        properties: [
                            {
                                name: submitData.name,
                                value: submitData.value,
                                page_number: submitData.pageNumber,
                            },
                        ],
                    });
                }
            };
        },
        [data?.field_groups, addCFProperty, createField]
    );

    // Filter fields based on search and foundToggle.
    // Scalar fields filtered at PROPERTY level; custom fields adapted inline.
    const filteredFieldGroups = useMemo(() => {
        if (!data?.field_groups) return [];

        return data.field_groups
            .map((group) => {
                // --- Scalar fields: filter at the property level ---
                const filteredScalarFields: IExtractedFieldWithMeta[] =
                    group.scalar_fields
                        .map((field) => {
                            const item = field.items[0];
                            if (!item) return null;

                            let properties = item.properties;
                            if (foundToggle === FoundToggleState.FOUND) {
                                properties = properties.filter(
                                    (p) => p.value !== null
                                );
                            } else if (
                                foundToggle === FoundToggleState.NOT_FOUND
                            ) {
                                properties = properties.filter(
                                    (p) => p.value === null
                                );
                            }

                            if (search) {
                                const searchLower = search.toLowerCase();
                                const fieldNameMatches = field.field_name
                                    .toLowerCase()
                                    .includes(searchLower);

                                if (!fieldNameMatches) {
                                    properties = properties.filter((p) => {
                                        const nameMatches = p.property_name
                                            .toLowerCase()
                                            .includes(searchLower);
                                        const valueMatches =
                                            p.value &&
                                            p.value
                                                .toLowerCase()
                                                .includes(searchLower);
                                        return nameMatches || valueMatches;
                                    });
                                }
                            }

                            if (properties.length === 0) return null;

                            const enriched = properties.map((p) => ({
                                ...p,
                                source: "extracted" as const,
                            }));

                            return {
                                ...field,
                                items: [{ ...item, properties: enriched }],
                            } as IExtractedFieldWithMeta;
                        })
                        .filter(Boolean) as IExtractedFieldWithMeta[];

                // --- Build lookup of attached custom fields by field_definition_id ---
                const attachedByFieldDef = new Map<
                    number,
                    ICustomFieldResponse[]
                >();
                const standaloneCustomFields: ICustomFieldResponse[] = [];

                for (const cf of group.custom_fields ?? []) {
                    if (cf.field_definition_id != null) {
                        const list =
                            attachedByFieldDef.get(cf.field_definition_id) ??
                            [];
                        list.push(cf);
                        attachedByFieldDef.set(cf.field_definition_id, list);
                    } else {
                        standaloneCustomFields.push(cf);
                    }
                }

                // --- Merge attached custom properties into matching extracted fields ---
                for (const field of filteredScalarFields) {
                    const attached = attachedByFieldDef.get(
                        field.field_definition_id
                    );
                    if (!attached?.length) continue;
                    const item = field.items[0];
                    if (!item) continue;
                    const customProps = attached.flatMap((cf) =>
                        cf.properties.map(adaptCustomProperty)
                    );
                    item.properties = [...item.properties, ...customProps];
                }

                // --- Adapt standalone custom fields to IExtractedFieldWithMeta ---
                const filteredStandalone = standaloneCustomFields.filter(
                    (cf) => {
                        if (foundToggle === FoundToggleState.NOT_FOUND)
                            return false;
                        if (search) {
                            const searchLower = search.toLowerCase();
                            const nameMatches = cf.name
                                .toLowerCase()
                                .includes(searchLower);
                            const valueMatches = cf.properties?.some(
                                (p) =>
                                    p.name
                                        ?.toLowerCase()
                                        .includes(searchLower) ||
                                    p.value?.toLowerCase().includes(searchLower)
                            );
                            if (!nameMatches && !valueMatches) return false;
                        }
                        return true;
                    }
                );
                const adaptedCustomFields: IExtractedFieldWithMeta[] =
                    filteredStandalone.map((cf) =>
                        adaptCustomFieldToExtracted(cf)
                    );

                // Append adapted custom fields after extracted scalar fields
                const mergedScalarFields = [
                    ...filteredScalarFields,
                    ...adaptedCustomFields,
                ];

                // --- Array fields: filter at the field level ---
                const filteredArrayFields = group.array_fields.filter(
                    (field) => {
                        const hasItems = field.items.length > 0;

                        if (foundToggle === FoundToggleState.FOUND && !hasItems)
                            return false;
                        if (
                            foundToggle === FoundToggleState.NOT_FOUND &&
                            hasItems
                        )
                            return false;

                        if (search) {
                            const searchLower = search.toLowerCase();
                            const nameMatches = field.field_name
                                .toLowerCase()
                                .includes(searchLower);
                            const itemMatches = field.items.some((item) => {
                                const displayNameMatches =
                                    item.display_name &&
                                    item.display_name
                                        .toLowerCase()
                                        .includes(searchLower);
                                const propertyMatches = item.properties.some(
                                    (p) =>
                                        p.value &&
                                        p.value
                                            .toLowerCase()
                                            .includes(searchLower)
                                );
                                return displayNameMatches || propertyMatches;
                            });
                            if (!nameMatches && !itemMatches) return false;
                        }

                        return true;
                    }
                );

                return {
                    ...group,
                    scalar_fields: mergedScalarFields,
                    array_fields: filteredArrayFields,
                    custom_fields: [],
                } as IDocumentFieldsByPolicyType;
            })
            .filter(
                (group) =>
                    group.scalar_fields.length > 0 ||
                    group.array_fields.length > 0
            );
    }, [data?.field_groups, search, foundToggle]);

    if (isLoading) {
        return (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
                <LoadingIndicator size={20} />
            </Box>
        );
    }

    if (!data?.field_groups.length) {
        return null;
    }

    if (filteredFieldGroups.length === 0) {
        return null;
    }

    return (
        <Stack>
            {filteredFieldGroups.map((group) => (
                <Accordion
                    key={group.policy_type_id}
                    disableGutters
                    elevation={0}
                    defaultExpanded
                >
                    <AccordionSummary
                        expandIcon={
                            <ExpandMoreIcon
                                sx={{
                                    color: "#94a3b8",
                                    fontSize: 18,
                                    transition: "color 0.15s ease",
                                }}
                            />
                        }
                        sx={{
                            minHeight: 52,
                            px: 2,
                            transition: "background 0.15s ease",
                            "&:hover": {
                                bgcolor: "rgba(34, 56, 118, 0.02)",
                                "& .MuiSvgIcon-root": {
                                    color: "#64748b",
                                },
                            },
                            "&.Mui-expanded": {
                                minHeight: 52,
                            },
                            "& .MuiAccordionSummary-content": {
                                my: 1.5,
                                "&.Mui-expanded": {
                                    my: 1.5,
                                },
                            },
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ width: "100%", pr: 0.5 }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "0.9375rem",
                                    color: "#0f172a",
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                {group.policy_type}
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minWidth: 24,
                                    height: 24,
                                    borderRadius: "50%",
                                    bgcolor: "#e2e8f0",
                                }}
                            >
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: "0.75rem",
                                        fontWeight: 500,
                                        color: "#64748b",
                                    }}
                                >
                                    {getFieldCount(group)}
                                </Typography>
                            </Box>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails
                        sx={{ bgcolor: "background.default", py: 1.5, px: 0 }}
                    >
                        <Box
                            sx={{
                                borderTop: 1,
                                borderBottom: 1,
                                borderColor: "divider",
                                overflow: "hidden",
                                bgcolor: "background.paper",
                                py: 1.5,
                                px: 1,
                            }}
                        >
                            <Stack spacing={0.5}>
                                {/* Scalar fields (includes adapted custom fields) */}
                                {group.scalar_fields.map((field) => {
                                    const meta =
                                        field as IExtractedFieldWithMeta;
                                    const addPropertyHandler =
                                        foundToggle !== FoundToggleState.FOUND
                                            ? undefined
                                            : meta._customFieldId
                                              ? handleAddProperty
                                              : makeAddPropertyToExtractedFieldHandler(
                                                    field.field_definition_id,
                                                    group.policy_type_id,
                                                    field.field_name
                                                );
                                    return (
                                        <DynamicScalarField
                                            key={
                                                meta._customFieldId
                                                    ? `custom-${meta._customFieldId}`
                                                    : field.field_definition_id
                                            }
                                            field={field}
                                            pageNavigationPluginInstance={
                                                pageNavigationPluginInstance
                                            }
                                            isEditable={isEditable}
                                            onSave={handleSave}
                                            onDelete={handleDelete}
                                            onPropertySave={handlePropertySave}
                                            onPropertyDelete={
                                                handlePropertyDelete
                                            }
                                            customFieldId={meta._customFieldId}
                                            onDeleteField={
                                                meta._customFieldId
                                                    ? () =>
                                                          deleteField.mutate(
                                                              meta._customFieldId!
                                                          )
                                                    : undefined
                                            }
                                            onAddProperty={addPropertyHandler}
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            isAddingProperty={
                                                addCFProperty.isPending ||
                                                createField.isPending
                                            }
                                        />
                                    );
                                })}

                                {/* Array fields */}
                                {group.array_fields.map((field) => (
                                    <DynamicArrayField
                                        key={field.field_definition_id}
                                        field={field}
                                        pageNavigationPluginInstance={
                                            pageNavigationPluginInstance
                                        }
                                        isEditable={isEditable}
                                        onSave={handleSave}
                                        onDelete={handleDelete}
                                        onDeleteItem={handleDeleteItem}
                                    />
                                ))}

                                {/* Add custom field popover, only in Found mode */}
                                {isEditable &&
                                    foundToggle === FoundToggleState.FOUND && (
                                        <Box sx={{ mt: 1.5, pl: 1 }}>
                                            <AddCustomFieldPopover
                                                scopeId={scope.id}
                                                documentId={scope.document.id}
                                                policyTypeId={
                                                    group.policy_type_id
                                                }
                                                isEditable={isEditable}
                                                onFieldCreated={() => {}}
                                                currentPage={currentPage}
                                                totalPages={totalPages}
                                            />
                                        </Box>
                                    )}
                            </Stack>
                        </Box>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Stack>
    );
};

// ---------------------------------------------------------------------------
// Type adapters
// ---------------------------------------------------------------------------

function adaptCustomProperty(
    cfProp: ICustomFieldPropertyResponse
): IExtractedPropertyValue {
    return {
        id: cfProp.id,
        property_name: cfProp.name,
        property_sort_order: 0,
        value: cfProp.value,
        raw_value: null,
        confidence: null,
        page_number: cfProp.page_number,
        is_edited: false,
        source: "custom",
    };
}

/** Adapt a standalone custom field to the extracted field shape for inline rendering. */
function adaptCustomFieldToExtracted(
    cf: ICustomFieldResponse
): IExtractedFieldWithMeta {
    return {
        field_definition_id: 0, // placeholder; _customFieldId is the real identifier
        field_name: cf.name,
        is_array: false,
        sort_order: Infinity,
        items: [
            {
                array_index: 0,
                display_name: null,
                properties: cf.properties.map(adaptCustomProperty),
            },
        ],
        _customFieldId: cf.id,
    };
}
